import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';

interface NegotiationData {
  id: string;
  status: string;
  relevancy: boolean;
  maxCPM: number;
  expectedCPM: number;
  extractedPrice: number;
  extractedAudience: any;
  averageViews: number;
}

const PriceNegotiation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  
  // Get data from location state
  const negotiationData = location.state?.responseData as NegotiationData;

  useEffect(() => {
    if (!negotiationData) {
      // If no data, redirect to home
      navigate('/');
    }
  }, [negotiationData, navigate]);

  const handleAccept = async () => {
    setLoading(true);
    setError('');

    try {
      // Calculate RPM rate (maxCPM / 2, rounded)
      const rpmRate = Math.round(negotiationData.maxCPM / 2);
      // Calculate max price (ceiling to nearest 500)
      const maxPrice = Math.ceil((negotiationData.extractedPrice || 1000) / 500) * 500;

      const response = await fetch(`${API_URL}/api/collaborations/start-auto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          responseId: negotiationData.id,
          sourceType: 'price_negotiated',
          pricingType: 'rpm',
          rpmRate: rpmRate,
          maxPrice: maxPrice
        })
      });

      const result = await response.json();

      if (result.success) {
        // Navigate to collaboration progress with the workflow data
        navigate('/collaboration-progress', { 
          state: { 
            workflowData: result.data,
            accessToken: result.accessToken
          }
        });
      } else {
        setError(result.error || 'Failed to start collaboration');
      }
    } catch (error) {
      console.error('Error accepting price:', error);
      setError('Failed to start collaboration');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    // Redirect to thank you page for manual review
    navigate('/thank-you');
  };

  if (!negotiationData) {
    return <div>Loading...</div>;
  }

  // Calculate our RPM rate (maxCPM / 2, rounded)
  const rpmRate = Math.round(negotiationData.maxCPM / 2);
  const requestedPrice = negotiationData.extractedPrice;
  const maxPrice = Math.ceil((requestedPrice || 1000) / 500) * 500;

  // Calculate estimated earnings range based on median views
  const estimatedEarnings = negotiationData.averageViews
    ? Math.round((negotiationData.averageViews * rpmRate) / 1000)
    : 0;

  // Parse audience data for display
  const audienceData = negotiationData.extractedAudience || {};
  const audienceEntries = Object.entries(audienceData).sort((a, b) => (b[1] as number) - (a[1] as number));

  // Country code to flag and name mapping
  const countryInfo: Record<string, { flag: string; name: string; cpm: number }> = {
    'US': { flag: '🇺🇸', name: 'United States', cpm: 2.50 },
    'GB': { flag: '🇬🇧', name: 'United Kingdom', cpm: 2.20 },
    'CA': { flag: '🇨🇦', name: 'Canada', cpm: 1.80 },
    'AU': { flag: '🇦🇺', name: 'Australia', cpm: 1.60 },
    'DE': { flag: '🇩🇪', name: 'Germany', cpm: 1.40 },
    'FR': { flag: '🇫🇷', name: 'France', cpm: 1.30 },
    'NL': { flag: '🇳🇱', name: 'Netherlands', cpm: 1.25 },
    'SE': { flag: '🇸🇪', name: 'Sweden', cpm: 1.20 },
    'NO': { flag: '🇳🇴', name: 'Norway', cpm: 1.15 },
    'DK': { flag: '🇩🇰', name: 'Denmark', cpm: 1.10 }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'white',
        padding: '2rem',
        border: '1px solid #e1e8ed',
        borderRadius: '12px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '0.5rem'
          }}>
            🤔 Let's discuss the pricing
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: '#666',
            lineHeight: '1.6'
          }}>
            Based on our analysis of your content performance and audience demographics:
          </p>
        </div>

        {error && (
          <div style={{
            background: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            border: '1px solid #f5c6cb'
          }}>
            {error}
          </div>
        )}

        {/* Pricing Calculation Box */}
        <div style={{
          background: '#f8f9fa',
          border: '2px solid #dee2e6',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{
            color: '#333',
            marginTop: 0,
            marginBottom: '1.5rem',
            textAlign: 'center',
            fontSize: '1.3rem'
          }}>
            📊 Pricing Calculation
          </h3>

          {/* Average Views */}
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ fontWeight: '600', color: '#495057', marginBottom: '0.5rem' }}>
              Average views (recent videos): {negotiationData.averageViews?.toLocaleString() || 'N/A'}
            </div>
          </div>

          {/* Audience Breakdown */}
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ fontWeight: '600', color: '#495057', marginBottom: '1rem' }}>
              Your audience breakdown:
            </div>
            {audienceEntries.slice(0, 5).map(([country, percentage]) => {
              const info = countryInfo[country] || { flag: '🏳️', name: country, cpm: 1.00 };
              return (
                <div key={country} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem 0',
                  borderBottom: '1px solid #f8f9fa'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.2rem' }}>{info.flag}</span>
                    <span>{info.name}</span>
                  </span>
                  <span style={{ color: '#6c757d' }}>
                    {((percentage as number) * 100).toFixed(1)}% (CPM: ${info.cpm.toFixed(2)})
                  </span>
                </div>
              );
            })}
            {audienceEntries.length > 5 && (
              <div style={{ padding: '0.5rem 0', color: '#6c757d', fontSize: '0.9rem' }}>
                + {audienceEntries.length - 5} other countries
              </div>
            )}
          </div>

          {/* RPM Calculation Result */}
          <div style={{
            background: '#e3f2fd',
            padding: '1.5rem',
            borderRadius: '8px',
            border: '2px solid #1976d2'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <span style={{ fontWeight: '600', color: '#1565c0' }}>
                Calculated Max CPM:
              </span>
              <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#1565c0' }}>
                ${negotiationData.maxCPM?.toFixed(2) || '0.00'}
              </span>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '1.3rem',
              fontWeight: 'bold',
              color: '#d32f2f',
              padding: '1rem 0',
              borderTop: '2px solid #bbdefb'
            }}>
              <span>Our RPM rate:</span>
              <span>${rpmRate} per 1,000 views</span>
            </div>

            <div style={{ fontSize: '0.9rem', color: '#1565c0', marginTop: '0.5rem' }}>
              (Based on your max CPM: ${negotiationData.maxCPM?.toFixed(2)} ÷ 2 = ${rpmRate})
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#fff',
              borderRadius: '6px',
              fontSize: '0.95rem',
              color: '#1565c0'
            }}>
              <strong>Estimated earnings:</strong> ${estimatedEarnings} per video
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                (Based on {negotiationData.averageViews?.toLocaleString()} avg views)
              </div>
              <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Maximum payment: ${maxPrice} per video
              </div>
            </div>
          </div>
        </div>

        {/* Price Comparison */}
        <div style={{
          background: '#fff3e0',
          border: '2px solid #ff9800',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '1.1rem'
          }}>
            <span style={{ color: '#e65100' }}>Your requested price:</span>
            <span style={{ fontWeight: 'bold', color: '#e65100' }}>${requestedPrice}</span>
          </div>
        </div>

        {/* Explanation */}
        <div style={{
          padding: '1.5rem',
          background: '#f1f8e9',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '1px solid #c8e6c9'
        }}>
          <p style={{ margin: 0, color: '#2e7d32', lineHeight: '1.6' }}>
            We're offering a <strong>performance-based RPM rate of ${rpmRate} per 1,000 views</strong> based on your audience
            demographics. Your actual payment will depend on video performance, with a maximum of <strong>${maxPrice} per video</strong>.
            This ensures fair compensation tied to the actual value your content generates.
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          marginTop: '2rem'
        }}>
          <button
            onClick={handleAccept}
            disabled={loading}
            style={{
              background: '#28a745',
              color: 'white',
              padding: '1rem 2rem',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
          >
            {loading ? 'Processing...' : `Accept RPM: $${rpmRate}/1k views`}
          </button>
          
          <button
            onClick={handleDecline}
            disabled={loading}
            style={{
              background: '#6c757d',
              color: 'white',
              padding: '1rem 2rem',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 0.2s'
            }}
          >
            Decline & Submit for Review
          </button>
        </div>

        {/* Additional Info */}
        <div style={{
          textAlign: 'center',
          marginTop: '2rem',
          color: '#6c757d',
          fontSize: '0.9rem'
        }}>
          <p>
            If you accept, we'll start the collaboration process immediately.<br/>
            If you decline, our team will review your request manually.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PriceNegotiation;